import { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { Link } from 'react-router-dom';
import { Box, Hidden, makeStyles } from '@material-ui/core';

import { labscoop_logo } from 'assets';
import MainHeader, { height } from 'layouts/MainHeader';
import Icon from 'components/Icon';
import IconButton from 'components/IconButton';

import Navigation from './Navigation';
import CardList from './CardsList';
import LabList from './LabList';
import HeaderSearch from './HeaderSearch';
import Profile from './Profile';

export { height };

const useStyles = makeStyles((theme) => ({
  root: () => ({
    minHeight: '100dvh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  }),
  logo: {
    '&::before': {
      display: 'none',
    },
    '& img': {
      width: 130,
      display: 'block',
    },
    [theme.breakpoints.down('xs')]: {
      flexGrow: 1,
      display: 'flex',
      alignItems: 'center',
    },
  },
  menuButton: {
    marginLeft: theme.spacing(1),

    [theme.breakpoints.down('xs')]: {
      color: '#3360AD',
      padding: 0,
      marginLeft: theme.spacing(2),
    },
  },
  profilePaper: {
    minWidth: 237,
    borderRadius: '8px 0 8px 8px',
  },
  profileList: {
    paddingTop: 0,
    '& a': {
      color: 'currentColor',
      display: 'flex',
      alignItems: 'center',
      flexGrow: 1,
      height: 36,
      '&::before': {
        display: 'none',
      },
    },
  },
  profileTitle: {
    height: height.desktop,
    background: 'rgba(30, 57, 102, 0.03)',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    padding: '8px 24px',
    fontWeight: 800,
    fontSize: 16,
    lineHeight: '24px',
    color: '#2D3543',
    position: 'relative',

    '&::before': {
      content: '""',
      display: 'block',
      width: 4,
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      backgroundColor: '#3360AD',
      borderRadius: '8px 0px 0px 8px',
    },
  },
  accountId: {
    width: '100%',
    fontSize: 12,
    color: '#5a6372',
  },
  iconButton: {
    width: 40,
    height: 40,
    padding: 4,
    '& svg': {
      width: '1em',
      height: 'auto',
      fontSize: 24,
    },
    [theme.breakpoints.down('xs')]: {
      width: 32,
      height: 32,
      padding: 0,
      '& svg': {
        fontSize: 32,
      },
    },
  },
  chemistryPaper: {
    minWidth: 237,
  },
  chemistryList: {
    paddingBottom: 0,
    '& .create-lab': {
      backgroundColor: 'rgba(30, 57, 102, 0.03)',
    },
  },
  homepage: {},
  hiddenSearch: {
    opacity: 0,
    pointerEvents: 'none',
  },
  authorization: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',

    '& a': {
      fontStyle: 'normal',
      fontWeight: 800,
      fontSize: 16,
      lineHeight: '16px',
      letterSpacing: '0.03em',
      whiteSpace: 'nowrap',
      marginLeft: '32px',
    },
  },
}));

const UserHeader = (props) => {
  const { children, loading, subheader, hideSearch, pending } = props;
  const [headerHeight, setHeaderHeight] = useState(0);
  const classes = useStyles({ headerHeight });
  const headerRef = useRef(null);

  const [navigationActive, setNavigationActive] = useState(false);

  useEffect(() => {
    let tid;

    const listenHeaderHeight = () => {
      clearTimeout(tid);

      // MUI needs a little time to refresh styles according to new screen size.
      // It is important to wait a little to get actual header height after styles
      // actualizing
      tid = setTimeout(() => {
        const { current: headerElement } = headerRef;

        if (headerElement) {
          const m = headerElement.getBoundingClientRect();
          setHeaderHeight(m.height);
        }
      }, 50);
    };
    window.addEventListener('resize', listenHeaderHeight);
    listenHeaderHeight();

    return () => {
      window.removeEventListener('resize', listenHeaderHeight);
      clearTimeout(tid);
    };
  }, []);

  return (
    <div className={classes.root} style={{ paddingTop: `${headerHeight}px` }}>
      <MainHeader
        loading={loading}
        subheader={subheader}
        ref={headerRef}
        border={false}
      >
        <Hidden xsDown>
          <Link to="/" className={classes.logo}>
            <img src={labscoop_logo} alt="Labscoop" />
          </Link>

          <IconButton
            className={classes.menuButton}
            size="small"
            onClick={() => {
              setNavigationActive((prev) => !prev);
            }}
          >
            <Icon.Burger fontSize={24} color="primary.dark" />
          </IconButton>

          <Box
            width="100%"
            ml={8}
            mr={14}
            className={cx(hideSearch && classes.hiddenSearch)}
          >
            <HeaderSearch disabled={pending} />
          </Box>

          <Box ml="auto">
            <Profile classes={classes} />
          </Box>

          <LabList classes={classes} />

          <CardList classes={classes} />
        </Hidden>

        <Hidden smUp>
          <Box flexGrow={1}>
            <Box display="flex" justifyContent="space-between" flexGrow={1}>
              <Link to="/" className={classes.logo}>
                <img src={labscoop_logo} alt="Labscoop" />
              </Link>

              <div>
                <CardList classes={classes} />
                <IconButton
                  aria-label="open drawer"
                  className={classes.menuButton}
                  onClick={() => {
                    setNavigationActive((prev) => !prev);
                  }}
                >
                  <Icon.Burger color="primary.main" fontSize={32} />
                </IconButton>
              </div>
            </Box>

            <Box mt={1.5} className={cx(hideSearch && classes.hiddenSearch)}>
              <HeaderSearch />
            </Box>
          </Box>
        </Hidden>
      </MainHeader>

      {children}

      <Navigation active={navigationActive} setActive={setNavigationActive} />
    </div>
  );
};

export default UserHeader;
